<script setup lang="ts">
import { computed } from 'vue'
import type { EnqueteFormQuestionUserVariableDefinitionFragment } from './EnqueteFormQuestion.generated'
import { stringifyUserVariableDataType } from '@/features/userVariable/dataType'
import type { Option } from '@/types/app'
import DolphinTextInput from '@/components/atoms/DolphinTextInput.vue'
import DolphinCheckbox from '@/components/atoms/DolphinCheckbox.vue'
import DolphinSelector from '@/components/atoms/DolphinSelector.vue'
import IconAngle from '@/components/icons/IconAngle.vue'
import IconClose from '@/components/icons/IconClose.vue'
import type {
  EnqueteFormErrorManager,
  EnqueteFormQuestionType,
} from './formSchema'
import EnqueteFormError from './EnqueteFormError.vue'

const questionModel = defineModel<EnqueteFormQuestionType>({ required: true })

const props = defineProps<{
  index: number
  userVariableDefinitions: EnqueteFormQuestionUserVariableDefinitionFragment[]
  shownControl: boolean
  formErrorManager: EnqueteFormErrorManager
}>()

const emit = defineEmits<{
  (event: 'movePrevious'): void
  (event: 'moveNext'): void
  (event: 'delete'): void
}>()

const isSavedQuestion = computed(() => questionModel.value.guid !== undefined)

const handleClickDeleteButton = () => {
  const confirmed = window.confirm('本当に削除しますか？')
  if (confirmed) {
    emit('delete')
  }
}

const userVariableDefinitionOptions = computed<Option<string>[]>(() => {
  return props.userVariableDefinitions.map(def => ({
    text: `${def.name}（${stringifyUserVariableDataType(def.value.__typename)}）`,
    value: def.guid,
  }))
})

const selectedUserVariableDefinition = computed(() => {
  return props.userVariableDefinitions.find(
    def => def.guid === questionModel.value.userVariableDefinitionGUID
  )
})
</script>

<template>
  <section :class="$style.question">
    <header :class="$style.header">
      <p :class="$style.heading">質問{{ props.index + 1 }}</p>
      <div v-if="props.shownControl" :class="$style.controls">
        <button
          @click="emit('movePrevious')"
          :class="$style.buttonMovePrevious"
        >
          <IconAngle />
        </button>
        <button @click="emit('moveNext')" :class="$style.buttonMoveNext">
          <IconAngle />
        </button>
        <button @click="handleClickDeleteButton" :class="$style.buttonDelete">
          <IconClose />
        </button>
      </div>
    </header>
    <div :class="$style.formRow">
      <p :class="$style.label">記録するカスタム項目</p>
      <p v-if="isSavedQuestion" :class="$style.savedQuestionText">
        {{
          userVariableDefinitionOptions.find(
            option => option.value === questionModel.userVariableDefinitionGUID
          )?.text
        }}
      </p>
      <DolphinSelector
        v-else
        :options="userVariableDefinitionOptions"
        v-model="questionModel.userVariableDefinitionGUID"
      />
      <EnqueteFormError
        v-if="
          formErrorManager.get(`questions.${index}.userVariableDefinitionGUID`)
        "
        >{{
          formErrorManager.get(`questions.${index}.userVariableDefinitionGUID`)
        }}</EnqueteFormError
      >
      <div
        v-if="
          selectedUserVariableDefinition &&
          (selectedUserVariableDefinition.value.__typename ===
            'UserVariableDefinitionValueSelect' ||
            selectedUserVariableDefinition.value.__typename ===
              'UserVariableDefinitionValueMultipleSelect')
        "
      >
        <p>選択肢</p>
        <ul :class="$style.options">
          <li
            v-for="option in selectedUserVariableDefinition.value.options"
            :key="option.guid"
            :class="$style.option"
          >
            {{ option.name }}
          </li>
        </ul>
      </div>
    </div>
    <div :class="$style.formRow">
      <p :class="$style.label">質問文</p>
      <DolphinTextInput v-model="questionModel.questionText" />
      <EnqueteFormError
        v-if="formErrorManager.get(`questions.${index}.questionText`)"
        >{{
          formErrorManager.get(`questions.${index}.questionText`)
        }}</EnqueteFormError
      >
    </div>
    <div :class="$style.formRow">
      <label :class="$style.isAnswerRequired">
        <DolphinCheckbox v-model="questionModel.isAnswerRequired" />
        <span>回答必須</span>
        <EnqueteFormError
          v-if="formErrorManager.get(`questions.${index}.isAnswerRequired`)"
          >{{
            formErrorManager.get(`questions.${index}.isAnswerRequired`)
          }}</EnqueteFormError
        >
      </label>
    </div>
  </section>
</template>

<style lang="scss" module>
.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.heading {
  font-size: 20px;
  font-weight: bold;
}

.controls {
  display: flex;
  align-items: center;
  gap: 16px;
}

.question {
  display: flex;
  flex-direction: column;
  gap: 24px;
  border: 1px solid $color-surface-tertiary;
  border-radius: 4px;
  padding: 16px;
  margin-top: 16px;
}

.formRow {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.label {
  color: $color-text-and-object-high-emphasis;
  font-size: 14px;
  font-weight: bold;
  line-height: 24px;
}

.savedQuestionText {
  font-size: 16px;
}

.isAnswerRequired {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.buttonMovePrevious {
  font-size: 18px;
  line-height: 18px;
  rotate: 270deg;

  svg {
    margin-right: 4px;
  }
}

.buttonMoveNext {
  font-size: 18px;
  line-height: 18px;
  rotate: 90deg;

  svg {
    margin-left: 4px;
  }
}

.buttonDelete {
  font-size: 24px;
  line-height: 24px;

  svg {
    margin-top: 4px;
  }
}

.options {
  display: block;
  list-style-type: disc;
  margin-block-start: 8px;
  padding-inline-start: 24px;
}

.option {
  list-style: disc;
  line-height: 1.8;
}
</style>
